import { useState } from "react";

import { getText, interpolateString } from "shared/lib";
import { type Message } from "shared/apiClient";
import {
  treeName,
  TreePropertyType,
  type CircumferenceTreePropertyDescriptor,
  type DBHTreePropertyDescriptor,
} from "@/components/treeModule/lib";

import { createMessageEvent, useChannel } from "shared/channel";
import {
  CaptureEventType,
  useAccount,
  useCaptureEvent,
  useNoticeboardData,
  useTreeModuleExpertResult,
} from "@/hooks";

import {
  NearbyTreesContainer,
  type NearbyTreesContainerProps,
} from "./NearbyTreesContainer";
import { QuestionLoader } from "@/components/wizard/questionLoader";

export interface NearbyTreesModuleStepProps {
  disabled?: boolean;
  message: Message;
  showLoading?: boolean;
}

export function NearbyTreesModuleStep({
  disabled,
  message,
  showLoading,
}: NearbyTreesModuleStepProps) {
  const { captureEvent } = useCaptureEvent();

  const { channel } = useChannel();

  const { conversation } = useAccount();
  const { noticeboardData } = useNoticeboardData();
  const treeModuleExpertResult = useTreeModuleExpertResult(
    conversation,
    noticeboardData,
  );

  const [submitting, setSubmitting] = useState(false);

  const handleClickNext: NearbyTreesContainerProps["onClickNext"] = (
    treeData,
  ) => {
    if (channel && conversation?.id) {
      setSubmitting(true);

      const text: Array<string> = [];

      if (treeData.areThereTreesNearby === "yes") {
        text.push(
          getText(
            "My proposed works will be nearby some trees, but I don't intend to remove or prune them. They have the following properties:",
          ),
        );

        treeData.treeDescriptors.forEach((treeDescriptor) => {
          const propertyMap = treeDescriptor.properties;

          text.push(`- ${treeName(treeDescriptor)}`);

          text.push(
            interpolateString("    - measurement type: {{measurementType}}", {
              measurementType: propertyMap.get(
                TreePropertyType.measurement_type,
              )?.value,
            }),
          );
          text.push(
            interpolateString("    - size: {{treeSize}}", {
              treeSize: propertyMap.get(TreePropertyType.size)?.value,
            }),
          );
          text.push(
            interpolateString("    - Trunk type: {{trunkType}}", {
              trunkType: propertyMap.get(TreePropertyType.trunk_type)?.value,
            }),
          );
          const dbhProperty = propertyMap.get(TreePropertyType.dbh) as
            | DBHTreePropertyDescriptor
            | undefined;
          if (dbhProperty) {
            text.push(
              interpolateString(
                "    - Diameter at {{distanceAboveGround}} above ground level: {{dbh}} m",
                {
                  distanceAboveGround: dbhProperty.distanceAboveGround,
                  dbh: dbhProperty.value,
                },
              ),
            );
          }
          const circumferenceProperty = propertyMap.get(
            TreePropertyType.circumference,
          ) as CircumferenceTreePropertyDescriptor | undefined;
          if (circumferenceProperty) {
            text.push(
              interpolateString(
                "    - Circumference at ground level: {{groundLevelCircumference}} m",
                {
                  groundLevelCircumference:
                    circumferenceProperty.groundLevelCircumference,
                },
              ) as string,
            );
            text.push(
              interpolateString(
                "    - Circumference at {{distanceAboveGround}} above ground level: {{circumference}} m",
                {
                  distanceAboveGround:
                    circumferenceProperty.distanceAboveGround,
                  circumference: circumferenceProperty.value,
                },
              ),
            );
          } else if (dbhProperty?.value) {
            const dbhValue = parseFloat(dbhProperty.value);
            const calculatedCircumference = (dbhValue * Math.PI).toFixed(2);
            text.push(
              interpolateString(
                "    - Circumference at {{distanceAboveGround}} above ground level: {{circumference}} m (calculated from diameter)",
                {
                  distanceAboveGround: dbhProperty.distanceAboveGround,
                  circumference: calculatedCircumference,
                },
              ),
            );
          }
          text.push(
            interpolateString("    - Distance from tree: {{distance}} m", {
              distance: propertyMap.get(TreePropertyType.distance)?.value,
            }),
          );
          text.push(
            interpolateString("    - Species: {{species}}", {
              species: propertyMap.get(TreePropertyType.species)?.value,
            }),
          );
          text.push(
            interpolateString(
              "    - Ground level alteration: {{groundLevelAlteration}}",
              {
                groundLevelAlteration: propertyMap.get(
                  TreePropertyType.ground_level_alteration,
                )?.value,
              },
            ),
          );

          text.push("\n");
        });

        if (treeData.areAnyTreesOutsidePropertyBoundary === "yes") {
          text.push(
            getText(
              "\nNote: Some of the trees are outside the property boundary.",
            ),
          );
        }
      } else {
        text.push(
          getText("My proposed works are not going to be nearby any trees"),
        );
      }

      const messageDescriptor = createMessageEvent({
        conversation_id: conversation.id,
        text: text.join("\n"),
      });

      console.log(
        "<NearbyTreesModuleStep> handleClickNext()",
        messageDescriptor,
      );
      channel.sendMessage(messageDescriptor);

      captureEvent(CaptureEventType.USER_ANSWERED_QUESTION, {
        messageId: message.id,
      });
    }
  };

  if (showLoading) {
    return <QuestionLoader />;
  }

  return (
    <NearbyTreesContainer
      councilCode={conversation?.council_code || ""}
      disabled={disabled}
      onClickNext={handleClickNext}
      showLoading={showLoading}
      submitting={submitting}
      treeModuleExpertResult={treeModuleExpertResult}
    />
  );
}
